import { getBackendLanguage, translateBackend } from '@assets/i18n/config';
import type { TranslatedString } from '@interfaces/shared';
import { BarChart } from '@ovComponents/2-component/barChart/barChart';
import { Card, CardContent } from '@ovComponents/2-component/card/card';
import {
  type ActiveOrganizationProps,
  UserContext,
} from '@providers/userContextProvider';
import { formatMoneyValue } from '@utils/moneyFormatting';
import dayjs from 'dayjs';
import { Box, Skeleton, Typography, useTheme } from 'hephaestus/web';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryBuilder } from './utils';

const totalManagementFeesQuery = (
  activeOrganization: ActiveOrganizationProps,
  startDate: string,
) => ({
  filters: [
    {
      field: 'organization',
      comparison: 'EQUALS',
      value: activeOrganization.id,
    },
    {
      field: 'type',
      comparison: 'EQUALS',
      value: 'MANAGEMENT_FEE',
    },
    {
      field: 'date',
      comparison: 'GREATER_THAN',
      value: startDate,
    },
  ],
  type: 'TRANSACTION',
  grouping: {
    field: 'date',
    interval: 'MONTH',
    type: 'DATE',
    valueAggregation: 'SUM',
    valueField: 'valueCents',
  },
});

type FeesAndRevenueTimeFrame = '1Y' | 'YTD';

export const FeesAndRevenue = ({
  options,
}: {
  options: {
    customTitle: TranslatedString;
  };
}) => {
  const defaultDate = dayjs().startOf('year').format('YYYY-MM-DD');

  const { t } = useTranslation(['homePageConfiguration', 'accountTypes']);
  const theme = useTheme();
  const { activeOrganization } = useContext(UserContext);
  const [totalValue, setTotalValue] = useState(0);
  const [timeFrame, setTimeFrame] = useState<FeesAndRevenueTimeFrame>('YTD');
  const [startDate, setStartDate] = useState<string>(defaultDate);
  const [formattedData, setFormattedData] = useState<any[]>([]);
  const [min, setMin] = useState<number | undefined>(undefined);

  const { customTitle } = options;

  const { data, loading, refetch } = useQueryBuilder(
    totalManagementFeesQuery(activeOrganization, startDate),
  );

  useEffect(() => {
    refetch();
  }, [activeOrganization, refetch, startDate]);

  useEffect(() => {
    const groupingData = [...(data?.generateReport?.grouping ?? [])];
    setTotalValue(
      groupingData.reduce((acc: number, curr: any) => acc + curr.value, 0) ?? 0,
    );
    if (groupingData.length > 0) {
      const minValue = Math.min(...groupingData.map((x: any) => x.value * -1));
      const maxValue = Math.max(...groupingData.map((x: any) => x.value * -1));
      const delta = maxValue - minValue;
      setMin(delta > maxValue ? minValue : undefined);

      let firstItemUTC = dayjs(new Date(groupingData[0].key)).utc();
      let lastItemUTC = dayjs(new Date(groupingData.slice(-1)[0].key)).utc();
      const currentMonth = dayjs().utc().startOf('month');
      const firstMonth = dayjs(startDate).startOf('month').utc();

      while (firstItemUTC.isAfter(firstMonth)) {
        const firstItem = firstItemUTC
          .subtract(1, 'month')
          .startOf('month')
          .valueOf();
        groupingData.unshift({
          key: firstItem,
          value: 0,
        });
        firstItemUTC = dayjs(new Date(firstItem)).utc();
      }

      while (lastItemUTC.isBefore(currentMonth)) {
        const nextItem = lastItemUTC.add(1, 'month').startOf('month').valueOf();
        groupingData.push({
          key: nextItem,
          value: 0,
        });
        lastItemUTC = dayjs(new Date(nextItem)).utc();
      }
      setFormattedData(groupingData);
    }
  }, [data]);

  const selectTimeFrame = (tf: FeesAndRevenueTimeFrame) => {
    setTimeFrame(tf);
    switch (tf) {
      case 'YTD':
        setStartDate(dayjs().startOf('year').format('YYYY-MM-DD'));
        break;
      case '1Y':
        setStartDate(
          dayjs().subtract(11, 'month').startOf('month').format('YYYY-MM-DD'),
        );
        break;
      default:
        setStartDate(defaultDate);
        break;
    }
  };

  return (
    <Card sx={{ mt: 2, height: '350px' }}>
      <CardContent
        sx={{
          paddingBottom: '16px! important',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          height: 'calc(100% - 32px)',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ mb: 2 }}>
            <Typography variant='titleLarge'>
              {translateBackend(customTitle)}
            </Typography>
            {loading ? (
              <Skeleton height='24px' width='150px' />
            ) : (
              <Typography
                variant='bodyLarge'
                color='textSecondary'
                sx={{ fontVariantNumeric: 'tabular-nums' }}
              >
                {formatMoneyValue(totalValue * -1)}
              </Typography>
            )}
          </Box>
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              marginRight: 6,
            }}
          >
            {(['1Y', 'YTD'] as FeesAndRevenueTimeFrame[]).map((tf) => (
              <Box
                key={tf}
                onClick={() => selectTimeFrame(tf)}
                sx={{
                  height: '22px',
                  borderRadius: theme.borderRadius.sm,
                  display: 'inline-flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                  color:
                    timeFrame === tf
                      ? theme.color.surface
                      : theme.color.onSurfaceVariant,
                  backgroundColor:
                    timeFrame === tf
                      ? theme.color.onSurface
                      : theme.color.surface,
                  ...(timeFrame === tf
                    ? {}
                    : {
                        '&:hover': {
                          backgroundColor: `${theme.color.outlineVariant} !important`,
                        },
                      }),
                }}
              >
                <Typography
                  variant='bodySmall'
                  component='p'
                  sx={{
                    color: timeFrame === tf ? theme.color.surface : undefined,
                    margin: '0.2em 1em',
                  }}
                >
                  {t(`feesAndRevenue.timeFrame.${tf}`)}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        <Box sx={{ flex: 1 }}>
          {loading ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'end',
                height: '100%',
                gap: 4,
              }}
            >
              <Skeleton variant='rectangular' height='20%' width='100%' />
              <Skeleton variant='rectangular' height='60%' width='100%' />
              <Skeleton variant='rectangular' height='100%' width='100%' />
              <Skeleton variant='rectangular' height='40%' width='100%' />
              <Skeleton variant='rectangular' height='80%' width='100%' />
              <Skeleton variant='rectangular' height='90%' width='100%' />
              <Skeleton variant='rectangular' height='40%' width='100%' />
            </Box>
          ) : totalValue === 0 ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
              }}
            >
              <Typography variant='bodyLarge' sx={{ fontWeight: 600 }}>
                {t('feesAndRevenue.noFeesCollectedYet')}
              </Typography>
            </Box>
          ) : (
            <BarChart
              labels={formattedData.map((x: any) =>
                dayjs(new Date(x.key))
                  .utc()
                  .locale(getBackendLanguage())
                  .format(`MMM ${timeFrame === '1Y' ? ' YYYY' : ''}`)
                  .toUpperCase(),
              )}
              datasets={[
                {
                  label: '',
                  data: formattedData.map((x: any) => x.value * -1),
                },
              ]}
              yAxis={{
                color: theme.color.onSurfaceVariant,
                font: {
                  family: theme.font.base,
                  size: 12,
                  weight: '600',
                },
                position: 'right',
                min,
              }}
              xAxis={{
                color: theme.color.onSurfaceVariant,
                font: {
                  family: theme.font.base,
                  size: 12,
                  weight: '600',
                },
                borderDisplay: false,
              }}
            />
          )}
        </Box>
      </CardContent>
    </Card>
  );
};
